<template>
  <el-form-item label="Targeting">
    <el-collapse v-model="activePanels">
      <el-collapse-item title="Spots & Domains" name="traffic_sources">
        <el-form-item label="Spots filter">
          <RonTypeSelector
            v-model="feed.ron_type"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="showSpotsSelector" label="Spots">
          <SpotsSelector
            v-model="feed.spot_ids"
            :stream="feed.stream"
            :type-id="feed.type_ids"
            :ignore-stream-filter="true"
            :disabled="false"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="showSpotsSelector" label="Saved Spots Lists">
          <WhiteListsSelector
            v-model="feed.spots_list_ids"
            :items="spotsLists"
            title="Select a saved list..."
            @update:modelValue="update"
          />
        </el-form-item>
        <hr />
        <el-form-item label="Sources quality">
          <QualitySelector
            v-model="feed.qualities"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item label="Subid filter">
          <el-radio-group
            v-model="feed.use_whitelist_subids"
            @update:modelValue="update"
          >
            <div class="domains-wrap">
              <el-radio :value="true">
                <span>Subid white list</span>
                <TextAreaWithArray
                  v-model="feed.whitelist_subids"
                  :disabled="!feed.use_whitelist_subids"
                  :rows="4"
                  @update:modelValue="update"
                />
                <p class="hint">
                  Allow traffic from listed hosts. One host per line.
                </p>
              </el-radio>
            </div>
            <div class="domains-wrap">
              <el-radio :value="false">
                <span>Subid black list</span>
                <TextAreaWithArray
                  v-model="feed.blacklist_subids"
                  :disabled="feed.use_whitelist_subids"
                  :rows="4"
                  @update:modelValue="update"
                />
                <p class="hint">
                  Disallow traffic from listed hosts. One host per line.
                </p>
              </el-radio>
            </div>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Domain filter">
          <el-radio-group
            v-model="feed.use_whitelist_domains"
            @update:modelValue="update"
          >
            <div class="domains-wrap">
              <el-radio :value="true">
                <span>Only listed domains</span>
                <TextAreaWithArray
                  v-model="feed.whitelist_domains"
                  :disabled="!feed.use_whitelist_domains"
                  :rows="4"
                  @update:modelValue="update"
                />
                <p class="hint">
                  One domain per line, e.g.: example.com (without https:// and
                  www)
                </p>
              </el-radio>
            </div>
            <div class="domains-wrap">
              <el-radio :value="false">
                <span>Exclude listed domains</span>
                <TextAreaWithArray
                  v-model="feed.blacklist_domains"
                  :disabled="feed.use_whitelist_domains"
                  :rows="4"
                  @update:modelValue="update"
                />
                <p class="hint">
                  One domain per line, e.g.: example.com (without https:// and
                  www)
                </p>
              </el-radio>
            </div>
          </el-radio-group>
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item title="Geo" name="geo">
        <CountrySelector
          v-model="feed.country_codes"
          @update:modelValue="update"
        />
      </el-collapse-item>
      <el-collapse-item title="Device" name="device">
        <DeviceSelector
          v-model="feed.device_ids"
          class="third-width-control"
          @update:modelValue="update"
        />
        <ConnectionTypeSelector
          v-model="feed.connection_type_ids"
          class="third-width-control"
          @update:modelValue="update"
        />
        <el-form-item label-width="150px" label="Platforms">
          <PlatformsSelector
            v-model="platforms"
            class="third-width-control"
            :with-specific="false"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item label-width="150px" label="Browsers">
          <BrowsersSelector
            v-model="browsers"
            class="third-width-control"
            :with-specific="false"
            @update:modelValue="update"
          />
        </el-form-item>
        <el-form-item v-if="isStaff" label="User-agent filter (admin only)">
          <UserAgentFilter v-model="userAgents" @update:modelValue="update" />
        </el-form-item>
      </el-collapse-item>
      <el-collapse-item title="Limits" name="limits">
        <FeedLimits v-model="feed.limits" @update:modelValue="update" />
      </el-collapse-item>
    </el-collapse>
  </el-form-item>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import CountrySelector from '@/apps/traforama/components/CountrySelector.vue'
import DeviceSelector from '@/apps/traforama/components/DeviceSelector.vue'
import ConnectionTypeSelector from '@/apps/traforama/components/ConnectionTypeSelector.vue'
import PlatformsSelector from '@/components/PlatformsSelector.vue'
import BrowsersSelector from '@/components/BrowsersSelector.vue'
import QualitySelector from '../../components/QualitySelector.vue'
import UserAgentFilter from '@/apps/traforama/components/UserAgentFilter.vue'
import FeedLimits from './FeedLimits.vue'
import RonTypeSelector from '@/apps/traforama/views/campaigns/components/RonTypeSelector.vue'
import SpotsSelector from '@/apps/traforama/components/SpotsSelector.vue'
import WhiteListsSelector from '@/apps/traforama/components/WhiteListsSelector.vue'

import TextAreaWithArray from '@/components/TextAreaWithArray.vue'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'
import { FeedValidationErrorsKey, RonType } from '@/api/types'
import { useSpotsListsStore } from '@/apps/traforama/store/spotsLists'

export default defineComponent({
  name: 'FeedTargeting',
  components: {
    CountrySelector,
    DeviceSelector,
    ConnectionTypeSelector,
    QualitySelector,
    BrowsersSelector,
    PlatformsSelector,
    TextAreaWithArray,
    UserAgentFilter,
    FeedLimits,
    RonTypeSelector,
    SpotsSelector,
    WhiteListsSelector
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const userStore = useUserStore()
    const spotsListsStore = useSpotsListsStore()

    const { isStaff } = userStore
    const feed = ref()
    feed.value = { ...props.modelValue }

    if (feed.value.ron_type === undefined || feed.value.ron_type === null) {
      feed.value.ron_type = RonType.All
    }

    const activePanels = reactive<Array<string>>([])

    const showSpotsSelector = computed(() => {
      return feed.value.ron_type !== RonType.All
    })

    const spotsLists = computed(() => {
      return Object.values(spotsListsStore.currentItems)
    })

    const platforms = computed({
      get: () => {
        return {
          platforms: feed.value.platform_ids
            ? feed.value.platform_ids.map((id: number | string) =>
                id.toString()
              )
            : []
        }
      },
      set: (val) => {
        feed.value.platform_ids = val.platforms
      }
    })

    const browsers = computed({
      get: () => {
        return {
          browsers: feed.value.browser_ids
            ? feed.value.browser_ids.map((id: number | string) => id.toString())
            : []
        }
      },
      set: (val) => {
        feed.value.browser_ids = val.browsers
      }
    })

    const userAgents = computed({
      get: () => {
        return {
          use_whitelist_user_agents: feed.value.use_whitelist_user_agents,
          whitelist_user_agents: feed.value.whitelist_user_agents,
          blacklist_user_agents: feed.value.blacklist_user_agents
        }
      },
      set: (val) => {
        feed.value.use_whitelist_user_agents = val.use_whitelist_user_agents
        feed.value.whitelist_user_agents = val.whitelist_user_agents
        feed.value.blacklist_user_agents = val.blacklist_user_agents
      }
    })

    const update = () => {
      const valueToUpdate = {
        connection_type_ids: feed.value.connection_type_ids,
        device_ids: feed.value.device_ids,
        country_codes: feed.value.country_codes,
        browser_ids: feed.value.browser_ids,
        qualities: feed.value.qualities,
        use_whitelist_domains: feed.value.use_whitelist_domains,
        blacklist_domains: feed.value.blacklist_domains,
        whitelist_domains: feed.value.whitelist_domains,
        use_whitelist_subids: feed.value.use_whitelist_subids,
        blacklist_subids: feed.value.blacklist_subids,
        whitelist_subids: feed.value.whitelist_subids,
        use_whitelist_user_agents: feed.value.use_whitelist_user_agents,
        whitelist_user_agents: feed.value.whitelist_user_agents,
        blacklist_user_agents: feed.value.blacklist_user_agents,
        platform_ids: feed.value.platform_ids,
        limits: feed.value.limits,
        ron_type: feed.value.ron_type,
        spot_ids: feed.value.spot_ids,
        spots_list_ids: feed.value.spots_list_ids
      }
      context.emit('update', valueToUpdate)
    }

    const { validationErrors, scrollToFirstError } = useValidation(
      FeedValidationErrorsKey
    )
    const errors = validationErrors()

    watch(
      errors,
      ({
        clicks_total,
        clicks_hourly,
        clicks_daily,
        budget_hourly,
        budget_daily,
        budget_total
      }) => {
        if (
          clicks_total ||
          clicks_hourly ||
          clicks_daily ||
          budget_hourly ||
          budget_daily ||
          budget_total
        ) {
          activePanels.push('limits')
          scrollToFirstError()
        }
      }
    )

    return {
      activePanels,
      browsers,
      userAgents,
      isStaff,
      feed,
      update,
      platforms,
      showSpotsSelector,
      spotsLists
    }
  }
})
</script>

<style lang="scss" scoped>
.desktop .el-collapse {
  margin-left: 2em;
}

.el-form-item__content > div {
  vertical-align: top;
}
:deep(.user-agents-wrap) {
  .el-radio {
    display: inline-block;
  }
}
.domains-wrap {
  margin: 13px 5px 5px 5px;
  width: 100%;
  :deep(.el-radio) {
    display: inline-block;
  }
  .el-textarea {
    margin-left: 5px;
    vertical-align: top;
    width: 450px;
  }

  .hint {
    line-height: 1.2;
    color: var(--el-text-color-secondary);
    margin-left: 142px;
  }
}
</style>
